import React, { useEffect, useState } from "react";
import "./OrdersSales.scss";

import { BsBank } from "react-icons/bs";
import { FaPercent, FaRegMoneyBillAlt, FaCheckCircle } from "react-icons/fa";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { setTaxRate } from "../../../api/settings";
import { toast } from "react-toastify";
import { updateTaxRate } from "../../../redux/features/organization-slice";
import useUserIsAllowed from "../../../helpers/userAuthorized";

const OrdersSales = () => {
  const {
    currency,
    tax_rate,
    id: organization_id,
  } = useSelector((state) => state.organizationReducer.value);
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [collectTaxRate, setCollectTaxRate] = useState(false);
  const [allowChangeFromCheckout, setAllowChangeFromCheckout] = useState(false);
  const [selectedTaxOption, setSelectedTaxOption] = useState("add_to_price");
  const [selectedTaxType, setSelectedTaxType] = useState("percentage");
  const [inputValue, setInputValue] = useState({
    value: 0,
    description: "",
  });

  const userIsAllowedToChangeTaxRate = useUserIsAllowed("administrator");

  useEffect(() => {
    setCollectTaxRate(tax_rate?.status || false);
    setAllowChangeFromCheckout(tax_rate?.allow_exemp || false);
    setSelectedTaxOption(tax_rate?.type);
    setSelectedTaxType(tax_rate?.category);
    setInputValue({
      value: tax_rate?.rate,
      description: tax_rate?.description,
    });
  }, [tax_rate]);

  const handleUpdateTaxInfo = () => {
    if (submitting) return;
    const data = {
      rate: inputValue.value,
      description: inputValue.description,
      status: collectTaxRate,
      allow_exemp: allowChangeFromCheckout,
      type: selectedTaxOption,
      category: selectedTaxType,
    };

    setSubmitting(true);

    setTaxRate(organization_id, data)
      .then((res) => {
        toast.dismiss(); toast.success("Sales tax details updated.");
        dispatch(updateTaxRate({ tax_rate: { ...tax_rate, ...data } }));
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          if (err?.response?.data?.message === "User not authorized!") {
            toast.dismiss(); toast.error(
              "You do not have sufficient permissions to perform this action. Please contact your administrator."
            );
          } else {
            toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
          }
        } else {
          toast.dismiss(); toast.error("Error while setting tax details.");
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  return (
    <div className="orders-sales-setting">
      <div className="container-wrapper orders-sales-wrapper">
        <div className="item-container">
          <ToggleSwitch
            name={"collect_sales_tax"}
            label={"Collect Sales Tax"}
            register={() => {}}
            required={false}
            SVG={BsBank}
            disabled={!userIsAllowedToChangeTaxRate}
            value={collectTaxRate}
            handleChange={() => {
              setCollectTaxRate((pre) => !pre);
            }}
          />

          <div className="options">
            <p
              className={collectTaxRate && userIsAllowedToChangeTaxRate ? "" : "disabled"}
              onClick={() => {
                collectTaxRate && userIsAllowedToChangeTaxRate && setSelectedTaxOption("add_to_price");
              }}
            >
              Added to the price
              {selectedTaxOption === "add_to_price" && (
                <FaCheckCircle className="check-svg" />
              )}
            </p>
            <p
              className={collectTaxRate && userIsAllowedToChangeTaxRate ? "" : "disabled"}
              style={{ borderRadius: "0 0 10px 10px" }}
              onClick={() => {
                collectTaxRate && userIsAllowedToChangeTaxRate && setSelectedTaxOption("include_in_price");
              }}
            >
              Included in price (VAT)
              {selectedTaxOption === "include_in_price" && (
                <FaCheckCircle className="check-svg" />
              )}
            </p>
          </div>
        </div>

        <div className="item-container">
          <div className="options">
            <p
              className={collectTaxRate && userIsAllowedToChangeTaxRate ? "" : "disabled"}
              style={{ border: "none", borderRadius: "10px 10px 0 0" }}
              onClick={() => {
                collectTaxRate && userIsAllowedToChangeTaxRate && setSelectedTaxType("percentage");
              }}
            >
              <FaPercent /> Percentage
              {selectedTaxType === "percentage" && (
                <FaCheckCircle className="check-svg" />
              )}
            </p>
            <p
              className={collectTaxRate && userIsAllowedToChangeTaxRate ? "" : "disabled"}
              style={{ borderRadius: "0 0 10px 10px" }}
              onClick={() => {
                collectTaxRate && userIsAllowedToChangeTaxRate && setSelectedTaxType("fixed");
              }}
            >
              <FaRegMoneyBillAlt /> Fixed
              {selectedTaxType === "fixed" && (
                <FaCheckCircle className="check-svg" />
              )}
            </p>
          </div>
        </div>

        <div className="item-container inputs">
          <input
            value={inputValue.description}
            disabled={!collectTaxRate || !userIsAllowedToChangeTaxRate}
            type="text"
            placeholder="Description"
            onChange={(e) => {
              setInputValue((pre) => {
                return { ...pre, description: e.target.value };
              });
            }}
          />
          <div className={`input-svg ${collectTaxRate ? "" : "disabled"}`}>
            <span>{selectedTaxType === "fixed" ? currency : "%"}</span>
            <input
              value={inputValue.value}
              disabled={!collectTaxRate || !userIsAllowedToChangeTaxRate}
              type="number"
              className="tax-price"
              onChange={(e) => {
                setInputValue((pre) => {
                  return { ...pre, value: e.target.value };
                });
              }}
            />
          </div>
        </div>

        <div className="item-container">
          <ToggleSwitch
            name={"allow_exemption"}
            label={AllowExemption}
            labelIsComponent={true}
            register={() => {}}
            required={false}
            disabled={!userIsAllowedToChangeTaxRate}
            value={allowChangeFromCheckout}
            handleChange={() => {
              setAllowChangeFromCheckout((pre) => !pre);
            }}
          />
        </div>

        <CustomButton
          name={`Save`}
          type={"button"}
          color={"primary"}
          bigButton={true}
          handleSubmit={handleUpdateTaxInfo}
          disabled={submitting || !userIsAllowedToChangeTaxRate}
          loader={submitting}
        />
      </div>
    </div>
  );
};

export default OrdersSales;

const AllowExemption = () => (
  <p
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "3px",
      alignItems: "flex-start",
      lineHeight: "19px",
    }}
  >
    Allow exemption{" "}
    <span style={{ fontSize: "8px", lineHeight: "9px", fontWeight: "300" }}>
      You'll be able to disable it during checkout.
    </span>
  </p>
);
